import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldAlert, ArrowLeft } from "lucide-react";
import { SERVICES, INDUSTRIES, TIER2_FAQ } from "@/data/content";
import { useSEO } from "@/lib/seo";
import { trackCTA } from "@/lib/analytics";
import CTASection from "@/components/CTASection";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const isTierII = slug === "tier-ii-reporting-support";

  useSEO({
    title: service ? service.name : "Service",
    description: service ? `${service.intro} ${service.keywords}` : "",
    path: `/services/${slug}`,
    jsonLd: service
      ? (slug === "tier-ii-reporting-support"
          ? [
              { "@context": "https://schema.org", "@type": "Service", name: service.name, description: service.intro, provider: { "@type": "Organization", name: "Wasserlab" }, areaServed: "US" },
              { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: TIER2_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
            ]
          : { "@context": "https://schema.org", "@type": "Service", name: service.name, description: service.intro, provider: { "@type": "Organization", name: "Wasserlab" }, areaServed: "US" })
      : null,
  });

  if (!service) return <Navigate to="/services" replace />;
  const Icon = service.icon;

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
          <Link to="/services" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white transition-colors" data-testid="back-to-services">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All Services
          </Link>
          <div className="mt-6 flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-amber-500 text-navy-900">
              <Icon className="h-7 w-7" aria-hidden="true" />
            </span>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Service</p>
          </div>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            {isTierII ? "Tier II Reporting Support for U.S. Facilities" : service.name}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">{service.intro}</p>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-navy-900">Overview</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-700">{service.body}</p>

            <h2 className="mt-12 font-heading text-2xl font-bold text-navy-900">What our support includes</h2>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {service.deliverables.map((d, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`deliverable-${i}`}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                  <span className="text-sm leading-relaxed text-slate-700">{d}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 rounded-xl border border-amber-200 bg-amber-50 p-6" data-testid="service-disclaimer">
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-slate-700">
                  <span className="font-bold text-navy-900">Important disclaimer:</span> Wasserlab provides technical and
                  compliance preparation support. Final regulatory determinations and submissions remain subject to client
                  review and applicable federal, state and local requirements.
                </p>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-xl border border-slate-200 bg-slate-50 p-6">
              <h3 className="font-heading text-lg font-bold text-navy-900">
                {isTierII ? "Check Your Tier II Readiness" : "Request a Free Assessment"}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Tell us about your facility and chemicals. Our compliance team will review and follow up.
              </p>
              <Link
                to="/free-tier-ii-assessment"
                onClick={() => trackCTA("Assessment", `service-${slug}`)}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-amber-500 px-5 py-3 text-sm font-bold text-navy-900 transition-transform hover:-translate-y-0.5 hover:bg-amber-400"
                data-testid="service-cta-assessment"
              >
                {isTierII ? "Request a Tier II Quote" : "Get a Free Tier II Assessment"}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 px-5 py-3 text-sm font-semibold text-navy-900 transition-colors hover:bg-white"
                data-testid="service-cta-consult"
              >
                Book a 20-Minute Consultation
              </Link>

              <div className="mt-8 border-t border-slate-200 pt-6">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-600">Related services</p>
                <ul className="mt-3 space-y-2">
                  {SERVICES.filter((s) => s.slug !== slug).slice(0, 4).map((s) => (
                    <li key={s.slug}>
                      <Link to={`/services/${s.slug}`} className="text-sm text-slate-600 hover:text-navy-900 transition-colors" data-testid={`related-service-${s.slug}`}>
                        {s.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {isTierII && (
        <section className="bg-slate-50 py-16 lg:py-20 border-t border-slate-200" data-testid="tierii-extended">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <h2 className="font-heading text-2xl font-bold text-navy-900">Our QA &amp; review process</h2>
            <p className="mt-3 text-base leading-relaxed text-slate-700">Every Tier II data package moves through structured preparation and a separate internal quality-assurance and technical review step — checking chemical identification, CAS numbers, and maximum/average quantity calculations — before it reaches your team for final review.</p>

            <h2 className="mt-10 font-heading text-2xl font-bold text-navy-900">State-specific considerations</h2>
            <p className="mt-3 text-base leading-relaxed text-slate-700">Requirements and reporting procedures vary by state and facility. Confirm applicable requirements with the relevant state/local authorities. We prepare submissions aligned to your state's portal and data fields where applicable.</p>

            <h2 className="mt-10 font-heading text-2xl font-bold text-navy-900">Typical turnaround</h2>
            <p className="mt-3 text-base leading-relaxed text-slate-700">Turnaround depends on inventory size, number of facilities and state-specific requirements. After we confirm scope and receive your documents, we provide an expected timeline for your engagement. We do not promise or guarantee any regulatory filing approval.</p>

            <h2 className="mt-10 font-heading text-2xl font-bold text-navy-900">Client responsibilities</h2>
            <p className="mt-3 text-base leading-relaxed text-slate-700">You provide accurate facility and chemical information and current SDS, review our prepared deliverables, and make the final regulatory determinations and submissions applicable to your facility.</p>

            <h2 className="mt-12 font-heading text-2xl font-bold text-navy-900">Frequently asked questions</h2>
            <div className="mt-6 space-y-3">
              {TIER2_FAQ.map((f, i) => (
                <details key={i} className="group rounded-xl border border-slate-200 bg-white p-5" data-testid={`tierii-faq-${i}`}>
                  <summary className="flex cursor-pointer items-center justify-between font-heading text-base font-bold text-navy-900 list-none">
                    {f.q}
                    <span className="ml-4 text-amber-500 transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection location={`service-${slug}-footer`} />
    </>
  );
}
